import { ReactNode, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { ContactProfile } from '../../api/contacts/types/response'
import properties from '../../api/properties/hooks'
import { Property, PropertyCriteria } from '../../api/properties/types/response'
import { getFilteredProperty } from '../../pages/Properties/utils'
import { DataTableLayout, RenderRowProps, useDataTable, useTableState } from '../TanstackTable'
import { useTableColumns } from './useTableColumns'

interface ContactProfileTableProps {
    isDemand: boolean
    contactId: string
    activeProfileId: string
    contactProfileItems: ContactProfile[]
    noResultMessage?: string
    toolbarActions?: () => ReactNode
}

const ContactProfileTable = ({
    isDemand,
    contactId,
    activeProfileId,
    contactProfileItems,
    noResultMessage,
    toolbarActions,
}: ContactProfileTableProps) => {
    const history = useHistory()

    const tableState = useTableState()

    const buildCriteria = (): Partial<PropertyCriteria> => {
        const serviceId = !isDemand ? contactProfileItems.find((p) => p.id.toString() === activeProfileId)?.contactProfileService?.id : null
        return (isDemand ? { contactProfile: activeProfileId } : { owner: contactId, propertyService: [serviceId] }) as Partial<PropertyCriteria>
    }

    const getPropertiesConfig = {
        params: {
            ...tableState.params,
            criteria: buildCriteria(),
        },
        isEnabled: {
            enabled: Boolean(activeProfileId),
        },
    }

    const { data: propertiesData, isLoading } = properties.GetProperties.useQuery(getPropertiesConfig.params, getPropertiesConfig.isEnabled)

    const propertiesItems = useMemo(() => propertiesData?.items ?? [], [propertiesData?.items])
    const pageCount = propertiesData?.pagination?.totalPages ?? 0

    const columns = useTableColumns()

    const { table, updateColumnSizing } = useDataTable({
        ...tableState,
        columns,
        data: propertiesItems,
        pageCount,
        ...(isDemand && {
            selectRow: {
                mapRow: getFilteredProperty,
            },
        }),
    })

    const renderRow = ({ row, children }: RenderRowProps<Property>) =>
        children({
            onClick: () => history.push(`/properties/${row.original.id}`),
            style: { cursor: 'pointer' },
        })

    return (
        <DataTableLayout
            table={table}
            isLoading={isLoading}
            totalItems={propertiesData?.count ?? 0}
            theadClass="dmn-table-header"
            noResultMessage={noResultMessage}
            toolbarActions={toolbarActions}
            updateColumnSizing={updateColumnSizing}
            renderRow={renderRow}
        />
    )
}

export default ContactProfileTable
